<!-- eslint-disable vue/no-v-html -->
<template>
  <div :class="className">
    <img v-if="!isUser && avatar" class="node-message__avatar" :src="avatar" alt="avatar" />
    <span v-if="!isUser && !avatar" class="node-message__avatar"> R </span>

    <div v-if="$slots.default" class="node-message__content">
      <slot />
    </div>

    <QuickPromptMessage v-else-if="isQuickPrompt" :message="message" />

    <div v-else class="node-message__content">
      <TextMessageWithTooltip :message="message" />
      <hr v-if="isQuickPrompt" class="divider" />
      <div v-if="hasData">
        <transition v-for="(blockData, index) in message.data" :key="index" name="slow-fade">
          <component
            v-bind="blockData"
            :is="getMessageBlock(blockData)"
            :data="blockData"
            :messageId="message.messageId"
          />
        </transition>
      </div>
      <hr v-if="showFeedback" class="divider" />
      <transition v-if="showFeedback" name="slow-fade">
        <Feedback :message-id="message.messageId" />
      </transition>
    </div>

    <span v-if="isUser" class="node-message__user-label"> You </span>
  </div>
</template>

<script>
import { computed } from 'vue';

import useAuthoring from '~/composables/useAuthoring';
import { Role } from '~/constants/conversationModels';

import arcaneLogo from '~/assets/svg/Arcane-logo.svg';
import chatbotLogo from '~/assets/svg/chatbot-logo.svg';

import QuickPromptMessage from '~/components/conversation/chatMessage/QuickPromptMessage';
import TextMessageWithTooltip from '~/components/conversation/chatMessage/TextMessageWithTooltip';
import ReferenceCardsList from '~/components/conversation/referencePanel/ReferenceCardsList';
import Feedback from '~/components/conversation/chatMessage/Feedback';
import TargetSourceTranslationBlock from '~/components/conversation/chatMessage/TargetSourceTranslationBlock';

export default {
  name: 'Message',
  components: {
    TargetSourceTranslationBlock,
    Feedback,
    ReferenceCardsList,
    TextMessageWithTooltip,
    QuickPromptMessage,
  },
  props: {
    message: {
      type: Object,
      default: () => {},
    },
  },

  setup(props) {
    const { isAuthoringPage } = useAuthoring();
    const isUser = computed(() => props.message.role === Role.user);
    const className = computed(() => `node-message ${isUser.value ? 'is-user' : ''}`);
    const avatar = computed(() => chatbotLogo);
    const hasData = computed(() => props.message.data && props.message.data.length > 0);
    const showFeedback = computed(
      () => !props.message.isTyping && !isUser.value && !props.message.systemMessage && !isAuthoringPage.value,
    );
    const isQuickPrompt = computed(() => !!props.message.versionContentId);

    const getMessageBlock = (blockData) => {
      let component = null;
      switch (blockData.type) {
        case 'documentTranslation':
        case 'documentAttachmentTranslation':
          component = TargetSourceTranslationBlock;
          break;
        case 'documentList':
          component = ReferenceCardsList;
          break;
        default:
          console.log('Invalid type of block message');
      }

      return component;
    };

    return {
      avatar,
      arcaneLogo,
      className,
      getMessageBlock,
      isUser,
      hasData,
      showFeedback,
      isQuickPrompt,
    };
  },
};
</script>

<style lang="scss" scoped>
.node-message {
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  padding-top: $bb-spacing-6;

  &__avatar {
    @include bb-medium-text-styles('xs');

    width: $bb-spacing-6;
    height: $bb-spacing-6;
    margin: 0 $bb-spacing-1 0 0;
    flex-shrink: 0;

    color: $bb-neutral-0;
    text-align: center;
    line-height: $bb-spacing-6;

    background: #ffff00;
    border-radius: $bb-spacing-6;
  }

  &__content {
    // max-width: min(242px, 100%);
    padding: $bb-spacing-3 $bb-spacing-4;
    line-height: normal;

    background: $bb-neutral-0;
    border: 1px solid $bb-neutral-4;
    border-radius: $bb-spacing-6 $bb-spacing-6 $bb-spacing-6 1px;
    max-width: 80%;
    display: flex;
    flex-direction: column;
    gap: $bb-spacing-2;
    // word-break: break-word;
    // white-space: pre-line;

    .slow-fade-enter-active,
    .slow-fade-leave-active {
      transition: opacity 3s; /* Adjust duration for slower/faster effect */
    }

    .slow-fade-enter-from,
    .slow-fade-leave-to {
      opacity: 2;
    }

    .divider {
      border: 0;
      height: 1px;
      background-color: #ccc;
      margin-top: $bb-spacing-1;
      margin-bottom: $bb-spacing-1;
    }
  }

  .highlighted {
    box-shadow: 0px 0px 8px rgba(65, 130, 239, 0.97);
  }

  .logo {
    height: 38px;
    cursor: pointer;
  }

  /* User message styling */
  &.is-user {
    flex-direction: row-reverse;
  }

  &.is-user &__content {
    color: $bb-neutral-1;
    border-radius: $bb-spacing-6 $bb-spacing-6 1px $bb-spacing-6;
    background-color: rgba(98, 34, 227, 0.1);
    // white-space: nowrap;
  }

  &__user-label {
    @include bb-regular-text-styles('xs');
    margin: 0 $bb-spacing-2 0 0;
    color: $bb-neutral-3;
  }
}
</style>
